import { useEffect } from 'react';
import type { TicketCustomerEmailStatus } from '../components/EmailEngagementPill';

const TERMINAL_STATES: TicketCustomerEmailStatus[] = [
  'clicked', 'complained', 'bounced', 'failed', 'not_sent',
];

/**
 * Polls for email engagement updates after a ticket email is sent.
 * - 5s interval for the first 60s
 * - 30s interval for the next 5 minutes
 * - Stops after 6 minutes total or on terminal status
 */
export function useTicketEngagementPolling(
  ticketId: string | undefined,
  status: TicketCustomerEmailStatus | undefined,
  refetch: () => Promise<unknown>,
) {
  useEffect(() => {
    if (!ticketId || !status) return;
    if (TERMINAL_STATES.includes(status)) return;

    const startedAt = Date.now();
    let timeout: ReturnType<typeof setTimeout>;
    let cancelled = false;

    function schedule() {
      if (cancelled) return;
      const elapsed = Date.now() - startedAt;
      const wait = elapsed < 60_000 ? 5_000
                 : elapsed < 5 * 60_000 ? 30_000
                 : 0;
      if (wait === 0) return;
      timeout = setTimeout(async () => {
        if (cancelled) return;
        await refetch();
        schedule();
      }, wait);
    }

    schedule();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [ticketId, status, refetch]);
}
